import type { ICapability } from '@/types/capability'
import type { IUser } from '@/types/user'
import type { IOrganization } from '@/types/organization'
import { CAPABILITIES_URL_MAP } from '@/lib/constants/capabilities'
import { hasPageAccess } from '@/lib/sidebar/getCapabilitySections'
import { isDemoOrganization } from '@/lib/utils'
import S from '@/settings'

export function isExcludedPage(pathname: string, excludedPages: string[]): boolean {
  return excludedPages.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

export function isPageAvailable(availableCapabilities: ICapability[], pathname: string): boolean {
  return availableCapabilities.some((capability) => CAPABILITIES_URL_MAP[capability].urls.includes(pathname))
}

export function isPageOpen(
  openCapabilities: ICapability[],
  pathname: string,
  user: IUser,
  isSuperAdminUser: boolean,
): boolean {
  const userOrganization = user.simulatedOrganization || user.organization
  if (isSuperAdminUser) return true
  if (S.MOCKED_PAGES.includes(pathname) && !isDemoOrganization(userOrganization)) return false
  // Organizations created after the backfill have no capabilities yet and keep the old columns
  const { capabilities } = userOrganization
  return capabilities ? hasPageAccess(capabilities, pathname) : hasPageCapability(openCapabilities, pathname)
}

// The per-organization part of isPageOpen: whether any of the capabilities opens the page
export function hasPageCapability(openCapabilities: ICapability[], pathname: string): boolean {
  return openCapabilities.some((openCapability) => {
    const capabilityConfig = CAPABILITIES_URL_MAP[openCapability]
    if (capabilityConfig.allowSlug) {
      const pathnameWithoutSlug = pathname.replace(/\/[^/]+$/, '')
      if (capabilityConfig.urls.includes(pathnameWithoutSlug)) return true
    }
    return capabilityConfig.urls.includes(pathname)
  })
}

export function isPageUnderMaintenance(
  pathname: string,
  organizationName: string,
  maintenancePages: string[],
  maintenanceOrganizations: string[],
): boolean {
  return maintenancePages.includes(pathname) && maintenanceOrganizations.includes(organizationName)
}

// An old capability is open when any of its pages is, until its checks move to sidebar sections
export function isCapabilityOpen(organization: IOrganization, capability: ICapability): boolean {
  const { capabilities, openCapabilities } = organization
  if (!capabilities) return openCapabilities?.includes(capability) ?? false
  return CAPABILITIES_URL_MAP[capability].urls.some((url) => hasPageAccess(capabilities, url))
}
